import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { StateStore } from './state-store';

export interface EntityCollectionState<TEntity> {
  entities: Readonly<Record<string, TEntity>>;
  ids: readonly string[];
  /** Waar tot de eerste set(), en opnieuw na reset() (zoals Akita). */
  loading: boolean;
  /** Wordt waar bij de eerste set(); reset() wist hem (Akita's cache-vlag). */
  hasCache: boolean;
}

export type EntityStoreState<TEntity, TExtra extends object = object> = EntityCollectionState<TEntity> &
  TExtra;

export type EntityComparer<TEntity> = (a: TEntity, b: TEntity) => number;

/**
 * Oplopend op één property, strings hoofdletterongevoelig: dezelfde vergelijking als Akita's
 * `@QueryConfig({ sortBy, sortByOrder: Order.ASC })`, zodat lijsten in dezelfde volgorde blijven.
 */
export function ascendingBy<TEntity>(key: keyof TEntity): EntityComparer<TEntity> {
  return (a, b) => {
    const valueA = normalizeForSort(a[key]);
    const valueB = normalizeForSort(b[key]);
    if (valueA > valueB) return 1;
    if (valueA < valueB) return -1;
    return 0;
  };
}

function normalizeForSort<TValue>(value: TValue): TValue | string {
  return typeof value === 'string' ? value.toUpperCase() : value;
}

/**
 * Collectie van entiteiten met id-sleutel (vervangt Akita's EntityStore + QueryEntity).
 * Volgorde = volgorde van set()/add(); sorteren gebeurt bij het lezen (getAll/selectAll).
 */
export class EntityStateStore<
  TEntity extends { id: string },
  TExtra extends object = object
> extends StateStore<EntityStoreState<TEntity, TExtra>> {
  constructor(createInitialExtra: () => TExtra = () => ({}) as TExtra) {
    super(() => ({ entities: {}, ids: [], loading: true, hasCache: false, ...createInitialExtra() }));
  }

  /** Vervangt de volledige collectie. */
  set(entities: readonly TEntity[]): void {
    const nextEntities: Record<string, TEntity> = {};
    for (const entity of entities) {
      nextEntities[entity.id] = entity;
    }
    this.setState({
      ...this.getValue(),
      entities: nextEntities,
      ids: [...new Set(entities.map(entity => entity.id))],
      loading: false,
      hasCache: true
    });
  }

  /** Voegt enkel entiteiten toe die er nog niet zijn (zoals Akita's add). */
  add(entityOrEntities: TEntity | readonly TEntity[]): void {
    const state = this.getValue();
    const incoming = Array.isArray(entityOrEntities) ? entityOrEntities : [entityOrEntities as TEntity];
    const nextEntities: Record<string, TEntity> = { ...state.entities };
    const addedIds: string[] = [];
    for (const entity of incoming) {
      if (Object.hasOwn(nextEntities, entity.id)) continue;
      nextEntities[entity.id] = entity;
      addedIds.push(entity.id);
    }
    if (!addedIds.length) return;
    this.setState({ ...state, entities: nextEntities, ids: [...state.ids, ...addedIds] });
  }

  /**
   * Zonder id: werkt de extra state bij (bv. filters). Met id: vervangt een bestaande entiteit
   * door de meegegeven versie; een onbekende id doet niets (zoals Akita).
   *
   * Akita bouwde de entiteit opnieuw op met `new constructor({ ...oud, ...nieuw })`. Alle
   * aanroepers geven een volledige entiteit van de server mee, dus vervangen geeft dezelfde
   * inhoud, en het werkt ook voor klassen met een positionele constructor (DeviceEntity, waar
   * Akita een kapotte entiteit van maakte).
   */
  update(partial: Partial<EntityStoreState<TEntity, TExtra>>): void;
  update(id: string, entity: TEntity): void;
  update(idOrPartial: string | Partial<EntityStoreState<TEntity, TExtra>>, entity?: TEntity): void {
    if (typeof idOrPartial !== 'string') {
      super.update(idOrPartial);
      return;
    }
    const state = this.getValue();
    if (!Object.hasOwn(state.entities, idOrPartial)) return;
    this.setState({ ...state, entities: { ...state.entities, [idOrPartial]: entity } });
  }

  /** Vervangt de entiteit als ze bestaat, voegt ze anders achteraan toe. */
  upsert(id: string, entity: TEntity): void {
    if (Object.hasOwn(this.getValue().entities, id)) {
      this.update(id, entity);
      return;
    }
    this.add(entity);
  }

  remove(id: string): void {
    const state = this.getValue();
    if (!Object.hasOwn(state.entities, id)) return;
    const { [id]: _removed, ...remaining } = state.entities;
    this.setState({ ...state, entities: remaining, ids: state.ids.filter(current => current !== id) });
  }

  getAll(compare?: EntityComparer<TEntity>): TEntity[] {
    const { ids, entities } = this.getValue();
    const all = ids.map(id => entities[id]);
    return compare ? all.sort(compare) : all;
  }

  /** Emitteert bij elke wijziging van de collectie, niet bij een wijziging van enkel de extra state. */
  selectAll(compare?: EntityComparer<TEntity>): Observable<TEntity[]> {
    return this.select(state => state.entities).pipe(map(() => this.getAll(compare)));
  }

  getEntity(id: string): TEntity | undefined {
    return this.getValue().entities[id];
  }

  selectEntity(id: string): Observable<TEntity | undefined> {
    return this.select(state => state.entities[id]);
  }

  selectLoading(): Observable<boolean> {
    return this.select(state => state.loading);
  }

  getHasCache(): boolean {
    return this.getValue().hasCache;
  }
}
